/**
 * Canonical PIM upload file groups (product gallery + product assets).
 * Keep in sync with rukh-api/app/Support/PimFileUploadGroups.php
 */

const IMAGE_EXTENSIONS = ['jpeg', 'jpg', 'png', 'gif', 'webp', 'avif'] as const

const ASSET_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
  'svg',
  'indd',
  'indt',
] as const

function uniqueSorted(extensions: readonly string[]): string[] {
  return [...new Set(extensions)].sort((a, b) => a.localeCompare(b))
}

function buildAcceptAttribute(imageExts: readonly string[], allExts: readonly string[]): string {
  const imageSet = new Set(imageExts)
  const nonImage = allExts.filter((e) => !imageSet.has(e))
  const dotted = nonImage.map((e) => `.${e}`)
  return ['image/*', ...dotted].join(',')
}

function formatExtensionList(extensions: readonly string[]): string {
  return extensions.map((e) => e.toUpperCase()).join(', ')
}

const imageAndAssetExtensions = uniqueSorted([...IMAGE_EXTENSIONS, ...ASSET_EXTENSIONS])

export const IMAGE_ONLY = {
  extensions: IMAGE_EXTENSIONS,
  acceptAttribute: 'image/*',
  labelShort: `Images (${formatExtensionList(IMAGE_EXTENSIONS)})`,
  labelDetailed: `Images: ${formatExtensionList(IMAGE_EXTENSIONS)}`,
} as const

export const ASSET_ONLY = {
  extensions: ASSET_EXTENSIONS,
  acceptAttribute: ASSET_EXTENSIONS.map((e) => `.${e}`).join(','),
  labelShort: `Documents (${formatExtensionList(ASSET_EXTENSIONS)})`,
  labelDetailed: `Documents & assets: ${formatExtensionList(ASSET_EXTENSIONS)}`,
} as const

/** Union of product gallery images and product asset uploads (PIM product module). */
export const IMAGE_AND_ASSET = {
  extensions: imageAndAssetExtensions as readonly string[],
  acceptAttribute: buildAcceptAttribute(IMAGE_EXTENSIONS, imageAndAssetExtensions),
  labelShort: 'Images and documents (same types as Product Development uploads)',
  labelDetailed: `Images: ${formatExtensionList(IMAGE_EXTENSIONS)}. Documents & assets: ${formatExtensionList(ASSET_EXTENSIONS)}.`,
} as const

/**
 * Mass PIM import (POST /products/import-media) — image workflow only.
 * Keep in sync with `ImportMediaService::IMAGE_EXTENSIONS` (PHP).
 */
export const IMPORT_MEDIA_MASS_IMPORT_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'] as const

/**
 * Mass PIM import — asset workflow only.
 * Keep in sync with `ImportMediaService::ASSET_EXTENSIONS` (PHP).
 */
export const IMPORT_MEDIA_MASS_IMPORT_ASSET_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'svg',
  'xlsx',
  'indd',
  'indt',
] as const

export type ImportMediaMassImportWorkflow = 'images' | 'assets' | 'mixed'

export const IMPORT_MEDIA_MASS_IMPORT_MIXED_EXTENSIONS = uniqueSorted([
  ...IMPORT_MEDIA_MASS_IMPORT_IMAGE_EXTENSIONS,
  ...IMPORT_MEDIA_MASS_IMPORT_ASSET_EXTENSIONS,
])

/** Universal Intake only — must match `PimFileUploadGroups::UNIVERSAL_INTAKE_EXTENSIONS` (PHP). */
const UNIVERSAL_INTAKE_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'webp',
  'pdf',
  'doc',
  'docx',
  'svg',
  'xlsx',
  'indd',
  'indt',
] as const

const UNIVERSAL_INTAKE_IMAGE_EXTENSIONS = ['jpeg', 'jpg', 'png', 'webp'] as const
const universalIntakeExtSet = new Set<string>(UNIVERSAL_INTAKE_EXTENSIONS)
const universalIntakeImageExtSet = new Set<string>(UNIVERSAL_INTAKE_IMAGE_EXTENSIONS)
const universalIntakeImageMimeSet = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
])

/**
 * Canonical per-file size cap (bytes) for product media + Product Dev / Universal Intake uploads.
 * Source of truth on the SPA; backend mirrors via `PimFileUploadGroups::PRODUCT_MEDIA_MAX_FILE_BYTES`.
 */
export const PRODUCT_MEDIA_MAX_FILE_BYTES = 100 * 1024 * 1024

/** Whole-number MB for UI strings (derived from {@link PRODUCT_MEDIA_MAX_FILE_BYTES}). */
export const PRODUCT_MEDIA_MAX_FILE_MB = 100

export const UNIVERSAL_INTAKE = {
  extensions: UNIVERSAL_INTAKE_EXTENSIONS,
  acceptAttribute: buildAcceptAttribute(UNIVERSAL_INTAKE_IMAGE_EXTENSIONS, UNIVERSAL_INTAKE_EXTENSIONS),
  labelShort: 'Universal Intake attachments',
  labelDetailed: `Allowed: ${formatExtensionList(UNIVERSAL_INTAKE_EXTENSIONS)}.`,
} as const

const DOC_MIME_TYPES = new Set<string>([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/csv',
  'image/svg+xml',
])

function getExtension(filename: string): string | undefined {
  const parts = filename.split('.')
  if (parts.length < 2) return undefined
  return parts.pop()?.toLowerCase()
}

const imageExtSet = new Set<string>(IMAGE_EXTENSIONS)
const assetExtSet = new Set<string>(ASSET_EXTENSIONS)

/**
 * Classify a file for Universal Intake / product-dev upload buckets.
 * SVG is a document (product assets), not a gallery image.
 */
export function classifyPimUploadFile(file: File): 'photo' | 'document' | 'other' {
  const ext = getExtension(file.name) ?? ''

  if (assetExtSet.has(ext)) {
    return 'document'
  }

  if (imageExtSet.has(ext)) {
    return file.type.startsWith('image/') ? 'photo' : 'other'
  }

  if (DOC_MIME_TYPES.has(file.type)) {
    return 'document'
  }

  if (file.type.startsWith('image/')) {
    return 'photo'
  }

  return 'other'
}

/** Client-side check aligned with PimFileUploadGroups::isValidUniversalIntakeFile (PHP). */
export function isAllowedPimImageAndAssetFile(file: File): boolean {
  const ext = getExtension(file.name) ?? ''
  if (!imageAndAssetExtensions.includes(ext)) {
    return false
  }
  if (imageExtSet.has(ext)) {
    return file.type.startsWith('image/')
  }
  return assetExtSet.has(ext)
}

/** Client-side check aligned with `PimFileUploadGroups::isValidUniversalIntakeFile` (Universal Intake only). */
export function isAllowedUniversalIntakeFile(file: File): boolean {
  const ext = getExtension(file.name) ?? ''
  if (!ext || !universalIntakeExtSet.has(ext)) {
    return false
  }

  const mime = (file.type || '').toLowerCase()

  if (universalIntakeImageExtSet.has(ext)) {
    if (mime === '' || mime === 'application/octet-stream') {
      return true
    }
    return universalIntakeImageMimeSet.has(mime)
  }

  if (ext === 'svg') {
    return (
      mime === '' ||
      mime === 'application/octet-stream' ||
      mime === 'text/xml' ||
      mime === 'text/plain' ||
      mime === 'image/svg+xml' ||
      mime.startsWith('image/svg')
    )
  }

  if (mime.startsWith('image/')) {
    return false
  }

  return true
}

/**
 * Classify Universal Intake staged files for `file_type` on upload (`photo` vs `document`).
 */
export function classifyUniversalIntakeUploadFile(file: File): 'photo' | 'document' | 'other' {
  const ext = getExtension(file.name) ?? ''

  if (universalIntakeImageExtSet.has(ext)) {
    const t = file.type
    if (!t || t === 'application/octet-stream' || t.startsWith('image/')) {
      return 'photo'
    }
    return 'other'
  }

  if (ext === 'svg' || ['pdf', 'doc', 'docx', 'xlsx', 'indd', 'indt'].includes(ext)) {
    return 'document'
  }

  return 'other'
}

/** Single API batch `file_type`: all photos → `photo`, otherwise `document` (mixed batches; MIME still stored per file). */
export function universalIntakeBatchFileType(files: File[]): 'photo' | 'document' {
  if (files.length === 0) {
    return 'document'
  }
  return files.every((f) => classifyUniversalIntakeUploadFile(f) === 'photo') ? 'photo' : 'document'
}
